import { ValueSetConcept } from '../types/fhir'
import { logger } from 'app/logging/Logger'
import { ApiHelper } from 'app/api/api-helper'
import { ApiRequester } from 'app/api/api-requestor'
import { ExpandValueSetResponse } from 'app/api/types'

/**
 * Service for fetching and caching FHIR ValueSets via backend proxy
 */

// In-memory cache for ValueSet expansions

// Cache expiry time (1 hour)
const CACHE_EXPIRY_MS = 60 * 60 * 1000

interface CacheEntry {
    concepts: ValueSetConcept[]
    timestamp: number
}

const cacheWithTimestamp = new Map<string, CacheEntry>()

/**
 * Fetch ValueSet expansion from backend proxy
 *
 * @param valueSetUrl - The canonical URL of the ValueSet
 * @param userId - The user ID for authentication
 * @returns Array of concepts from the ValueSet expansion
 */
export async function fetchValueSet(
    valueSetUrl: string,
    userId: string
): Promise<ValueSetConcept[]> {
    // Check cache first
    const cached = getCachedValueSet(valueSetUrl)
    if (cached) {
        logger.debug('ValueSet found in cache', {
            action: 'valueset_cache_hit',
            metadata: { valueSetUrl, conceptCount: cached.length },
        })
        return cached
    }

    logger.debug('Fetching ValueSet from backend', {
        action: 'valueset_fetch_start',
        metadata: { valueSetUrl, userId },
    })

    try {
        // Call backend proxy endpoint
        const operation = ApiHelper.buildExpandValueSetOperation(
            valueSetUrl,
            userId
        )

        const response =
            await ApiRequester.callPostOperationRequest<ExpandValueSetResponse>(
                operation
            )

        if (!response.concepts || response.concepts.length === 0) {
            logger.warn('ValueSet has no concepts', {
                action: 'valueset_no_concepts',
                metadata: { valueSetUrl },
            })
            return []
        }

        const concepts = response.concepts

        // Cache the result
        cacheValueSet(valueSetUrl, concepts)

        logger.debug('ValueSet fetched successfully', {
            action: 'valueset_fetch_success',
            metadata: {
                valueSetUrl,
                conceptCount: concepts.length,
            },
        })

        return concepts
    } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error')
        logger.error('Failed to fetch ValueSet', err, {
            action: 'valueset_fetch_error',
            metadata: { valueSetUrl, error: err.message },
        })
        throw err
    }
}

/**
 * Get cached ValueSet if available and not expired
 */
function getCachedValueSet(valueSetUrl: string): ValueSetConcept[] | null {
    const entry = cacheWithTimestamp.get(valueSetUrl)

    if (!entry) {
        return null
    }

    // Check if cache is expired
    const now = Date.now()
    if (now - entry.timestamp > CACHE_EXPIRY_MS) {
        logger.debug('ValueSet cache expired', {
            action: 'valueset_cache_expired',
            metadata: { valueSetUrl },
        })
        cacheWithTimestamp.delete(valueSetUrl)
        return null
    }

    return entry.concepts
}

/**
 * Cache ValueSet concepts with timestamp
 */
function cacheValueSet(valueSetUrl: string, concepts: ValueSetConcept[]): void {
    cacheWithTimestamp.set(valueSetUrl, {
        concepts,
        timestamp: Date.now(),
    })

    logger.debug('ValueSet cached', {
        action: 'valueset_cached',
        metadata: {
            valueSetUrl,
            conceptCount: concepts.length,
        },
    })
}

/**
 * Clear all cached ValueSets
 */
export function clearValueSetCache(): void {
    cacheWithTimestamp.clear()
    logger.debug('ValueSet cache cleared', {
        action: 'valueset_cache_cleared',
    })
}

/**
 * Prefetch multiple ValueSets in parallel
 * Useful for preloading all ValueSets for a Questionnaire
 */
export async function prefetchValueSets(
    valueSetUrls: string[],
    userId: string
): Promise<Map<string, ValueSetConcept[]>> {
    logger.debug('Prefetching ValueSets', {
        action: 'valueset_prefetch_start',
        metadata: { count: valueSetUrls.length, urls: valueSetUrls, userId },
    })

    const results = new Map<string, ValueSetConcept[]>()

    // Fetch all in parallel
    const promises = valueSetUrls.map(async (url) => {
        try {
            const concepts = await fetchValueSet(url, userId)
            results.set(url, concepts)
        } catch {
            logger.warn('Failed to prefetch ValueSet', {
                action: 'valueset_prefetch_failed',
                metadata: { url },
            })
            // Don't throw - continue with other ValueSets
            results.set(url, [])
        }
    })

    await Promise.all(promises)

    logger.debug('ValueSets prefetched', {
        action: 'valueset_prefetch_complete',
        metadata: { successCount: results.size },
    })

    return results
}
